// route-level code splitting
import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { toNonVueRoute } from './utils';

const Credits = () => import('@/pages/CRM/Credits/CreditsOverview.vue');
const CRMView = () => import('@/pages/CRM/index.vue');
const CustomerList = () => import('@/pages/CRM/Customers/CustomerList.vue');
const ContactList = () => import('@/pages/CRM/Contacts/ContactList.vue');
const ContactAddEdit = () => import('@/pages/CRM/Contacts/ContactAddEdit.vue');
const DistributorZoneList = () => import('@/pages/CRM/DistributorZones/DistributorZoneList.vue');
const DistributorZoneAddEdit = () => import('@/pages/CRM/DistributorZones/DistributorZoneAddEdit.vue');
const ImportCustomers = () => import('@/pages/CRM/Upload/ImportCustomers.vue');
const TaskList = () => import('@/pages/CRM/Tasks/TaskList.vue');
const GroupList = () => import('@/pages/CRM/Groups/GroupList.vue');
const FindBuyer = () => import('@/pages/CRM/FindBuyer/FindBuyer.vue');

export function CRMTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.CUSTOMERS },
      label: i18n.t('common.customers'),
    },
    {
      to: { name: ROUTE_NAMES.CONTACTS },
      label: i18n.t('common.contacts'),
    },
    {
      to: { name: ROUTE_NAMES.TASKS },
      label: i18n.t('common.tasks'),
    },
    {
      to: { name: ROUTE_NAMES.CALENDAR },
      label: i18n.t('common.calendar'),
    },
    {
      to: { name: ROUTE_NAMES.SERVICE_ZONES },
      label: i18n.t('common.service_zone'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.CUSTOMER_STATUSES },
      label: i18n.t('crm.overview.customer_statuses'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.SALES_FUNNEL },
      label: i18n.t('crm.overview.sales_funnel'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.TEMPLATES },
      label: i18n.t('common.templates'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.MANAGE_TERRITORIES },
      label: i18n.t('common.territories'),
      visible: ({ userPermissions }) => {
        return userPermissions?.enableTerritories && userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.DISTRIBUTION_ZONES },
      label: i18n.t('crm.distributionZones'),
      visible: ({ userPermissions }) => {
        return userPermissions?.enableZoneFeatures && userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.CREDITS },
      label: i18n.t('crm.credits.credits'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
    {
      to: { name: ROUTE_NAMES.GROUPS },
      label: i18n.t('common.groups'),
      visible: ({ userPermissions }) => {
        return userPermissions?.noLimitedAccess;
      },
    },
  ]);
}

const crmRoutes: RouteRecordRaw[] = [
  // Legacy Routes
  toNonVueRoute({ path: 'contacts/:contactId/relationships/', name: ROUTE_NAMES.CONTACT_RELATIONSHIPS }),
  toNonVueRoute({ path: 'customers/calendar/', name: ROUTE_NAMES.CALENDAR }),
  toNonVueRoute({ path: 'service-zones/', name: ROUTE_NAMES.SERVICE_ZONES }),
  toNonVueRoute({ path: 'customer-statuses/', name: ROUTE_NAMES.CUSTOMER_STATUSES }),
  toNonVueRoute({ path: 'customers/companies/:customerId(\\d+)/', name: ROUTE_NAMES.CUSTOMER_COMPANY }),
  toNonVueRoute({ path: 'customers/companies/:customerId/#activity', name: ROUTE_NAMES.ADD_ACTIVITY }),
  toNonVueRoute({ path: 'customers/companies/:customerId/edit/', name: ROUTE_NAMES.CUSTOMER_EDIT }),
  toNonVueRoute({ path: 'customers/companies/:customerId/transfer-data/', name: ROUTE_NAMES.TRANSFER_DATA }),
  toNonVueRoute({ path: 'customers/companies/add/', name: ROUTE_NAMES.CUSTOMER_ADD }),
  toNonVueRoute({ path: 'customers/companies/add/?partner_id=:customerId', name: ROUTE_NAMES.CUSTOMER_ADD_WITH_ID }),
  toNonVueRoute({ path: 'customers/sales-funnel/', name: ROUTE_NAMES.SALES_FUNNEL }),
  toNonVueRoute({ path: 'customers/templates/', name: ROUTE_NAMES.TEMPLATES }),
  toNonVueRoute({ path: 'customers/manage-territories/', name: ROUTE_NAMES.MANAGE_TERRITORIES }),
  toNonVueRoute({ path: 'customers/bulk-reassign/', name: ROUTE_NAMES.BULK_REASSIGN }),

  {
    component: CRMView,
    path: 'customers/',
    beforeEnter: async (_to, _from) => {
      await loadTranslations('crm');
    },
    meta: {
      tabs: CRMTabRoutes,
    },
    children: [
      // Customers
      {
        path: '',
        name: ROUTE_NAMES.CUSTOMERS,
        component: CustomerList,
        meta: {
          title: () => i18n.t('crm.overview.title'),
          description: () => i18n.t('crm.overview.description'),
          permissions: [
            {
              rules: ['canManageCrm', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'import',
        name: ROUTE_NAMES.IMPORT,
        component: ImportCustomers,
        meta: {
          title: () => i18n.t('crm.csvImport.importCustomers'),
          permissions: [
            {
              rules: [
                'canManageCrm',
                ({ userActiveCompany, user }) =>
                  Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
                  Boolean(
                    user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true,
                  ),
              ],
            },
          ],
        },
      },
      {
        path: 'companies/find-buyer/',
        name: ROUTE_NAMES.FIND_BUYER,
        component: FindBuyer,
        meta: {
          title: () => i18n.t('crm.findBuyer.title'),
          description: () => i18n.t('crm.findBuyer.description'),
        },
      },

      // Contacts
      {
        path: 'contacts',
        name: ROUTE_NAMES.CONTACTS,
        component: ContactList,
        meta: {
          title: (activeCompany) =>
            i18n.t('crm.contacts_title', {
              name: activeCompany.name,
            }),
          description: (activeCompany) =>
            i18n.t('crm.contacts_description', {
              name: activeCompany.name,
            }),
          permissions: [
            {
              rules: ['canManageCrm', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'contacts/add',
        name: ROUTE_NAMES.CONTACTS_ADD,
        component: ContactAddEdit,
        meta: {
          title: () => i18n.t('crm.add_contact'),
          permissions: [
            {
              rules: ['canManageCrm', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },
      {
        path: 'contacts/:contactId/edit',
        name: ROUTE_NAMES.CONTACTS_EDIT,
        component: ContactAddEdit,
        props: true,
        meta: {
          title: () => i18n.t('crm.edit_contact'),
          permissions: [
            {
              rules: ['canManageCrm', ({ userActiveCompany }) => Boolean(userActiveCompany?.seller)],
            },
          ],
        },
      },

      // Tasks
      {
        path: 'tasks',
        name: ROUTE_NAMES.TASKS,
        component: TaskList,
        meta: {
          title: () => i18n.t('common.tasks'),
          description: () => i18n.t('crm.tasks.description'),
        },
      },

      // Credits
      {
        path: 'credits',
        name: ROUTE_NAMES.CREDITS,
        component: Credits,
        meta: {
          title: () => i18n.t('crm.credits.credits'),
          description: () => i18n.t('crm.credits.manage_credits'),
          permissions: [
            {
              rules: [
                'canManageCrm',
                ({ userActiveCompany, user }) =>
                  Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
                  Boolean(
                    user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true,
                  ),
              ],
            },
          ],
        },
      },

      // Zones
      {
        path: 'distribution-zones',
        name: ROUTE_NAMES.DISTRIBUTION_ZONES,
        component: DistributorZoneList,
        meta: {
          title: () => i18n.t('crm.distributionZones'),
          description: () => i18n.t('crm.zones.description_overview'),
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller)],
            },
          ],
        },
      },
      {
        path: 'distribution-zones/add',
        name: ROUTE_NAMES.DISTRIBUTION_ZONES_ADD,
        component: DistributorZoneAddEdit,
        meta: {
          title: () => i18n.t('crm.zones.titleAdd'),
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller)],
            },
          ],
        },
      },
      {
        path: 'distribution-zones/:id/edit',
        name: ROUTE_NAMES.DISTRIBUTION_ZONES_EDIT,
        component: DistributorZoneAddEdit,
        props: true,
        meta: {
          title: () => i18n.t('crm.zones.titleEdit'),
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller)],
            },
          ],
        },
      },

      // Groups
      {
        path: 'groups',
        name: ROUTE_NAMES.GROUPS,
        component: GroupList,
        meta: {
          title: () => i18n.t('crm.groups.title'),
          description: () => i18n.t('crm.groups.description'),
          permissions: [
            {
              rules: [
                'canManageCrm',
                ({ userActiveCompany, user }) =>
                  Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
                  Boolean(
                    user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true,
                  ),
              ],
            },
          ],
        },
      },
    ],
  },
];

export default crmRoutes;
